import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import { setBookingDetails } from '../../lib/store/bookingSlice';

/**
 * Custom hook for handling booking form submission
 */
export const useBookingSubmit = (formData, validateForm, setIsClicked) => {
    const router = useRouter();
    const dispatch = useDispatch();

    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        setIsClicked(true);

        const isFormValid = validateForm();

        if (isFormValid) {
            // Store form data in Redux
            // Convert 12-hour format to 24-hour format for storage
            const departureTime24h = moment(formData.departureTime, 'hh:mm A').format('HH:mm');
            const returnTime24h = moment(formData.returnTime, 'hh:mm A').format('HH:mm');
            
            const dateFormat = 'YYYY-MM-DD HH:mm';
            const pickupDate = moment.utc(formData.departureDate + ' ' + departureTime24h, dateFormat);
            const returnDate = moment.utc(formData.returnDate + ' ' + returnTime24h, dateFormat);

            const bookingData = {
                ...formData,
                departureUnix: pickupDate.unix(),
                departureString: pickupDate.format(dateFormat),
                returnUnix: returnDate.unix(),
                returnString: returnDate.format(dateFormat),
            };

            dispatch(setBookingDetails(bookingData));

            // Send Google Tag Manager event after validation
            if (typeof window !== 'undefined' && window.gtag && typeof window.gtag === 'function') {
                try {
                    const analytics = {
                        pickUpAddress: '',
                        dropOffAddress: '',
                        departureDate: '',
                        returnDate: '',
                        noOfPassengers: 0,
                        tripType: '',
                    };
                    
                    const pickUpAddress = formData.pickUpAddress;
                    const dropOffAddress = formData.dropOffAddress;
                    const { departureDate, returnDate, noOfPassengers, tripType } = formData;
                    
                    analytics.pickUpAddress = pickUpAddress?.formatted_address || pickUpAddress || '';
                    analytics.dropOffAddress = dropOffAddress?.formatted_address || dropOffAddress || '';
                    analytics.departureDate = moment.utc(formData.departureDate + ' ' + departureTime24h, dateFormat);
                    analytics.returnDate = moment.utc(formData.returnDate + ' ' + returnTime24h, dateFormat);
                    analytics.noOfPassengers = parseInt(noOfPassengers) || 0;
                    analytics.tripType = tripType === '1' ? 'One Way' : 'Return';

                    
                    window.gtag('event', 'TRIP_FORM_FILLED', {
                        event_category: 'Trip_form_filled',
                        event_label: 'trip_booking_form',
                        value: analytics,
                    });

                } catch (trackingError) {
                    console.error('❌ GTM tracking error:', trackingError);
                    // Don't block form submission if tracking fails
                }
            } else {
                console.warn('⚠️ GTM not available - window.gtag not found');
            }

            // Redirect to booking information page
            router.push('/booking-information');
        }
    }, [formData, validateForm, setIsClicked, dispatch, router]);

    return {
        handleSubmit,
    };
};
